import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star, Bookmark, Radio, MessageCircle, X } from "lucide-react";
import { toast } from "sonner";

export default function SavedMessages() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/bookmarks")
      .then(({ data }) => { if (alive) setItems(data.bookmarks || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const remove = async (message_id) => {
    try {
      await api.delete(`/bookmarks/${encodeURIComponent(message_id)}`);
      setItems((xs) => xs.filter((x) => x.message_id !== message_id));
      toast.success("Removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove.");
    }
  };

  // Group by "source_type::source_id"
  const groups = items.reduce((acc, b) => {
    const key = `${b.source_type}::${b.source_id}`;
    (acc[key] = acc[key] || { source_type: b.source_type, source_id: b.source_id, label: b.source_label, items: [] }).items.push(b);
    return acc;
  }, {});

  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8" data-testid="saved-messages">
        <div className="flex items-center gap-2 mb-6">
          <Bookmark className="w-5 h-5 text-acid" />
          <h1 className="font-heading text-3xl md:text-4xl leading-none">SAVED MESSAGES</h1>
        </div>

        {user?.is_guest && (
          <div className="border-2 border-blaze/60 p-4 font-mono text-sm text-blaze/90">
            // register to save messages across sessions
          </div>
        )}

        {loading ? (
          <div className="font-mono text-white/40 text-sm">// loading…</div>
        ) : Object.keys(groups).length === 0 ? (
          <div className="border-2 border-white/10 bg-panel/40 p-6 font-mono text-sm text-white/40">
            // no bookmarks yet · tap the <Star className="inline w-3 h-3 mx-1 text-acid" /> icon on any message to save it
          </div>
        ) : (
          <div className="space-y-8">
            {Object.values(groups).map((g) => (
              <section key={`${g.source_type}::${g.source_id}`} data-testid={`saved-group-${g.source_type}-${g.source_id}`}>
                <div className="flex items-center gap-2 mb-2 font-mono text-[10px] text-white/50 uppercase tracking-widest">
                  {g.source_type === "room" ? <Radio className="w-3 h-3" /> : <MessageCircle className="w-3 h-3" />}
                  {g.source_type === "room" ? `# ${g.label}` : `DM · @${g.label}`}
                  <span className="text-white/30">· {g.items.length}</span>
                </div>
                <div className="border-2 border-white/10 bg-panel/40 divide-y divide-white/10">
                  {g.items.map((b) => {
                    const href = b.source_type === "room"
                      ? `/room/${b.source_id}?highlight=${b.message_id}`
                      : `/dm/${b.source_id}?highlight=${b.message_id}`;
                    return (
                      <div key={b.id} className="flex items-start gap-3 p-3 md:p-4 hover:bg-white/[0.02] group" data-testid={`saved-item-${b.message_id}`}>
                        <Link to={href} className="flex-1 min-w-0">
                          <div className="font-mono text-[10px] text-white/40">@{b.from_username} · {new Date(b.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                          <div className="font-body text-sm mt-1 text-white/90 line-clamp-2">
                            {b.kind === "image" ? "🖼 image" : b.kind === "audio" ? "🎙 voice note" : (b.body_snapshot || "// (empty)")}
                          </div>
                        </Link>
                        <button
                          type="button"
                          onClick={() => remove(b.message_id)}
                          className="text-white/30 hover:text-blaze shrink-0"
                          title="Remove"
                          data-testid={`saved-remove-${b.message_id}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
